// @flow
import React, { useState, useMemo, useCallback } from 'react';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import type { Target } from '../../../../types';
import { TYPE } from '../../../../constants/targetConstants';
import { degToRad } from '../../../../utils';
import { fromLatLon } from '../../../../utils';
import { useResolution } from '../../../ui/openlayers';
import type { Style } from '../../../ui/openlayers';
import Feature from '../../../ui/openlayers/Feature';
import { useBaseResolution } from '../styleContext';
import { getStyle, useDraggableLabelStyle } from '../style';

const CONNECTOR_PROPERTIES = {
    car: {
        type: 'labelConnector',
        targetType: 'car'
    },
    plane: {
        type: 'labelConnector',
        targetType: 'plane'
    }
};

type DraggableLabelStyle = (label: string) => Style;

export type Props = $ReadOnly<{|
    targetType: $Values<typeof TYPE>,
    target: Target,
    selected: boolean,
    dataSource: 'auto' | 'system' | 'device',
    labelStyles: $ReadOnly<{
        default: $ReadOnly<{
            auto: DraggableLabelStyle,
            system: DraggableLabelStyle,
            device: DraggableLabelStyle
        }>,
        selected: $ReadOnly<{
            auto?: DraggableLabelStyle,
            system: DraggableLabelStyle,
            device?: DraggableLabelStyle
        }>
    }>,
    connectorStyles: $ReadOnly<{
        default: $ReadOnly<{
            auto?: Style,
            system: Style,
            device?: Style
        }>,
        selected: $ReadOnly<{
            auto?: Style,
            system: Style,
            device?: Style
        }>
    }>,

    onSelect: () => void,
    onDeselect: () => void
|}>;

function DraggableLabel({ targetType, target, selected, dataSource, labelStyles, connectorStyles, onSelect, onDeselect }: Props) {
    const resolution = useResolution();
    const coefficient = Math.pow(resolution || 1, 1 / 1.5);
    const [offset, setOffset] = useState([70, 70]);
    const [draggingGeometry, setDraggingGeometry] = useState();
    const [draggingCoordinates, setDraggingCoordinates] = useState();
    const label = target.TargetIdentification;
    const style = useDraggableLabelStyle(getStyle(labelStyles, selected, dataSource), { label });
    const connectorStyle = getStyle(connectorStyles, selected, dataSource);

    const geometry = useMemo(() => {
        if(draggingGeometry) {
            return draggingGeometry;
        }
        const coords = fromLatLon(target.MovementData.Position);
        return new Point([coords[0] + (offset[0] * coefficient), coords[1] + (offset[1] * coefficient)]);
    }, [target, coefficient, offset, draggingGeometry]);
    const properties = useMemo(() => ({
        type: 'draggableLabel',
        targetType: targetType,
        icao: target.Icao,
        label: target.TargetIdentification
    }), [targetType, target]);
    const connectorGeometry = useMemo(() => new LineString([fromLatLon(target.MovementData.Position), draggingCoordinates || geometry.getCoordinates()]), [target, geometry, draggingCoordinates]);
    const connectorProperties = CONNECTOR_PROPERTIES[targetType];

    const handleDragStart = () => setDraggingGeometry(geometry);
    const handleOnDrag = () => setDraggingCoordinates(draggingGeometry && draggingGeometry.getCoordinates());
    const handleDragEnd = () => {
        if(!draggingGeometry) {
            throw new Error('Dragging geometry is not defined');
        }

        const targetCoordinates = fromLatLon(target.MovementData.Position);
        const labelCoordinates = draggingGeometry.getCoordinates();
        setOffset([(labelCoordinates[0] - targetCoordinates[0]) / coefficient, (labelCoordinates[1] - targetCoordinates[1]) / coefficient]);
        setDraggingGeometry(null);
        setDraggingCoordinates(null);
    };

    return (
        <React.Fragment>
            <Feature
                geometry={geometry}
                properties={properties}
                style={style}
                onSelect={onSelect}
                onDeselect={onDeselect}
                onDragStart={handleDragStart}
                onDrag={handleOnDrag}
                onDragEnd={handleDragEnd}
            />
            <Feature
                geometry={connectorGeometry}
                properties={connectorProperties}
                style={connectorStyle}
            />
        </React.Fragment>
    );
}

export default DraggableLabel;